import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { TemplateResponse } from '@troia-v3/types';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { StatusPill } from '@/shared/components/ui/StatusPill';
import { Icon } from '@/shared/icons';
import {
  getTemplateTypeInfo,
  getProviderLabel,
  getTemplateStatusMeta,
  getTemplatePreview,
} from './templateListShared';

/**
 * Linha de tabela de template (view "Lista"). Padrão-lista travado:
 * checkbox de seleção (1ª col) + colunas multi-linha (nome + prévia) + menu ••• (última col).
 */
export interface TemplateListItemProps {
  template: TemplateResponse;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
  onDelete?: (id: string) => void;
  onDuplicate?: (id: string) => void;
}

export const TemplateListItem: React.FC<TemplateListItemProps> = ({
  template,
  selected = false,
  onToggleSelect,
  onDelete,
  onDuplicate,
}) => {
  const navigate = useNavigate();

  const typeInfo = getTemplateTypeInfo(template);
  const statusMeta = getTemplateStatusMeta(template.status);
  const providerLabel = getProviderLabel(template.providerConfig?.providerType);
  const preview = getTemplatePreview(template);
  const variableCount = template.variables?.length ?? 0;

  const actions: CardAction[] = [
    { label: 'Visualizar', icon: 'eye', onClick: () => navigate(`/prospecting/templates/${template.id}/preview`) },
    { label: 'Editar', icon: 'edit', onClick: () => navigate(`/prospecting/templates/${template.id}/edit`) },
  ];
  if (onDuplicate) {
    actions.push({ label: 'Duplicar', icon: 'copy', onClick: () => onDuplicate(template.id) });
  }
  if (onDelete) {
    actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onDelete(template.id), variant: 'danger' });
  }

  return (
    <tr
      className="border-b transition-colors"
      style={{ borderColor: 'var(--theme-card-border)', backgroundColor: selected ? 'var(--theme-primary-background)' : undefined }}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'transparent'; }}
      data-testid={`template-row-${template.id}`}
    >
      {/* Checkbox */}
      <td className="pl-4 pr-1 py-3 w-11">
        <button
          type="button"
          onClick={() => onToggleSelect?.(template.id)}
          className="w-[18px] h-[18px] rounded-[5px] border-[1.6px] flex items-center justify-center transition-colors"
          style={{
            backgroundColor: selected ? 'var(--theme-primary)' : 'transparent',
            borderColor: selected ? 'var(--theme-primary)' : 'var(--theme-input-border)',
            color: '#ffffff',
          }}
          aria-label={selected ? 'Desmarcar' : 'Selecionar'}
          data-testid={`template-check-${template.id}`}
        >
          {selected && <Icon name="check" className="w-3 h-3" />}
        </button>
      </td>

      {/* Nome: ícone de tipo + nome + prévia/descrição */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${typeInfo.color}18`, color: typeInfo.color }}
          >
            <Icon name={typeInfo.icon} className="w-4 h-4" style={{ color: typeInfo.color }} />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
              {template.name}
            </div>
            {preview && (
              <div className="text-xs truncate max-w-xs" style={{ color: 'var(--theme-muted-text)' }}>
                {preview}
              </div>
            )}
          </div>
        </div>
      </td>

      {/* Provider */}
      <td className="px-4 py-3">
        <span
          className="inline-flex items-center gap-1.5 h-[23px] px-2.5 rounded-md text-xs font-medium"
          style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-card-text)' }}
        >
          <Icon name={typeInfo.icon} className="w-3.5 h-3.5" style={{ color: typeInfo.color }} />
          {providerLabel}
        </span>
      </td>

      {/* Status */}
      <td className="px-4 py-3">
        <StatusPill
          variant={statusMeta.variant}
          label={statusMeta.label}
          data-testid={`template-status-${template.id}`}
        />
      </td>

      {/* Variáveis */}
      <td className="px-4 py-3 text-sm tabular-nums" style={{ color: 'var(--theme-card-text)' }}>
        {variableCount}
      </td>

      {/* Criado em */}
      <td className="px-4 py-3 text-sm whitespace-nowrap" style={{ color: 'var(--theme-muted-text)' }}>
        {new Date(template.createdAt).toLocaleDateString('pt-BR')}
      </td>

      {/* Ações */}
      <td className="px-4 py-3 text-right w-14">
        <CardActionsMenu actions={actions} size="sm" data-testid={`template-actions-${template.id}`} />
      </td>
    </tr>
  );
};

TemplateListItem.displayName = 'TemplateListItem';
